const SHELL_ICONS = Object.freeze({
  coin: new URL('../../assets/ui/icons/coin.svg', import.meta.url).href,
  gem: new URL('../../assets/ui/icons/gem.svg', import.meta.url).href,
  play: new URL('../../assets/ui/shell/play.svg', import.meta.url).href,
  map: new URL('../../assets/ui/shell/map.svg', import.meta.url).href,
  settings: new URL('../../assets/ui/shell/settings.svg', import.meta.url).href,
  help: new URL('../../assets/ui/shell/help.svg', import.meta.url).href,
  home: new URL('../../assets/ui/shell/home.svg', import.meta.url).href,
  lock: new URL('../../assets/ui/shell/lock.svg', import.meta.url).href,
  star: new URL('../../assets/ui/shell/star.svg', import.meta.url).href,
  trophy: new URL('../../assets/ui/shell/trophy.svg', import.meta.url).href,
  avatar: new URL('../../assets/concept/bolt-forge-mockup-01.png', import.meta.url).href,
});

const HOME_BACKDROP = new URL('../../assets/concept/bolt-forge-mockup-01.png', import.meta.url).href;
const MAP_BACKDROP = new URL('../../assets/concept/bolt-forge-mockup-03.png', import.meta.url).href;

const CAMPAIGN_LEVEL_COUNT = 20;
const CAMPAIGN_STAR_MAX = CAMPAIGN_LEVEL_COUNT * 3;

function el(tag, className = '', attrs = {}) {
  const node = document.createElement(tag);
  if (className) node.className = className;
  for (const [key, value] of Object.entries(attrs)) {
    if (key === 'text') node.textContent = value;
    else if (key === 'html') node.innerHTML = value;
    else if (key === 'style') {
      for (const [styleKey, styleValue] of Object.entries(value || {})) {
        if (styleKey.startsWith('--')) node.style.setProperty(styleKey, styleValue);
        else node.style[styleKey] = styleValue;
      }
    }
    else if (key === 'disabled') node.disabled = Boolean(value);
    else node.setAttribute(key, value);
  }
  return node;
}

export function levelId(displayNumber) {
  return `level-${String(displayNumber).padStart(3, '0')}`;
}

export function summarizeProgress(progress, campaignLevels = CAMPAIGN_LEVEL_COUNT) {
  const completed = progress?.completed || {};
  const records = Array.from({ length: campaignLevels }, (_, index) => completed[levelId(index + 1)])
    .filter((record) => record?.completed);
  const totalStars = records.reduce((sum, record) => sum + Math.max(0, Number(record.stars) || 0), 0);
  const completedCount = records.length;
  const unlockedLevel = Math.max(1, Math.min(campaignLevels, Number(progress?.unlockedLevel) || 1));
  const playerLevel = Math.max(1, 1 + Math.floor(totalStars / 3));
  return { completedCount, totalStars, unlockedLevel, playerLevel };
}

function mountShell(root, { screen, backdrop }) {
  root.replaceChildren();
  const viewport = el('main', `bf-shell-viewport bf-shell-viewport--${screen}`, { 'data-shell-screen': screen });
  const stage = el('section', `bf-shell-stage bf-shell-stage--${screen}`);
  const atmosphere = el('div', 'bf-shell-atmosphere', {
    style: { backgroundImage: `url("${backdrop}")` },
    'aria-hidden': 'true',
  });
  const glow = el('div', 'bf-shell-glow', { 'aria-hidden': 'true' });
  stage.append(atmosphere, glow);
  viewport.append(stage);
  root.append(viewport);
  return stage;
}

function currencyPill(kind, amount) {
  const pill = el('div', `bf-shell-currency bf-shell-currency--${kind}`);
  pill.append(
    el('img', 'bf-shell-currency__icon', { src: SHELL_ICONS[kind], alt: '' }),
    el('strong', 'bf-shell-currency__value', { text: amount }),
    el('span', 'bf-shell-plus', { text: '+' }),
  );
  return pill;
}

function profileHud(summary) {
  const hud = el('header', 'bf-shell-hud');
  const profile = el('div', 'bf-shell-profile');
  profile.append(
    el('img', 'bf-shell-profile__avatar', { src: SHELL_ICONS.avatar, alt: 'ForgeMaster' }),
    el('div', 'bf-shell-profile__copy', {
      html: `<strong>ForgeMaster</strong><span>Lv. ${summary.playerLevel}</span>`,
    }),
  );
  const currencies = el('div', 'bf-shell-currencies');
  currencies.append(currencyPill('coin', '12,480'), currencyPill('gem', '320'));
  hud.append(profile, currencies);
  return hud;
}

function forgedButton(label, icon, className, onClick, attrs = {}) {
  const button = el('button', `bf-shell-button ${className}`.trim(), { type: 'button', ...attrs });
  if (icon) button.append(el('img', 'bf-shell-button__icon', { src: icon, alt: '' }));
  button.append(el('strong', 'bf-shell-button__label', { text: label }));
  button.addEventListener('click', onClick);
  return button;
}

function miniatureBolt(color = 'steel') {
  return el('span', `bf-home-mini-bolt bf-home-mini-bolt--${color}`, {
    html: '<i></i>',
    'aria-hidden': 'true',
  });
}

function buildHomeSculpture() {
  const sculpture = el('div', 'bf-home-sculpture', { 'aria-hidden': 'true' });
  const support = el('div', 'bf-home-sculpture__support');
  const red = el('div', 'bf-home-sculpture__bar bf-home-sculpture__bar--red');
  const blue = el('div', 'bf-home-sculpture__bar bf-home-sculpture__bar--blue');
  const gold = el('div', 'bf-home-sculpture__bar bf-home-sculpture__bar--gold');
  red.append(miniatureBolt('steel'), miniatureBolt('red'), miniatureBolt('steel'));
  blue.append(miniatureBolt('blue'), miniatureBolt('green'), miniatureBolt('steel'));
  gold.append(miniatureBolt('gold'), miniatureBolt('steel'), miniatureBolt('gold'));
  support.append(miniatureBolt('steel'), miniatureBolt('gold'));
  sculpture.append(support, red, blue, gold);
  return sculpture;
}

export function renderHome(root, {
  progress,
  onPlay,
  onMap,
  onHelp,
  onSettings,
} = {}) {
  const summary = summarizeProgress(progress);
  const stage = mountShell(root, { screen: 'home', backdrop: HOME_BACKDROP });
  stage.append(profileHud(summary));

  const brand = el('section', 'bf-home-brand');
  brand.innerHTML = `
    <div class="bf-home-logo" aria-label="Bolt Forge">
      <span class="bf-home-logo__bolt">BOLT</span>
      <span class="bf-home-logo__forge">FORGE</span>
    </div>
    <p>SOLVE <b>•</b> UNSCREW <b>•</b> BUILD A BRIGHTER MIND</p>
  `;

  const progressCard = el('div', 'bf-home-progress-card');
  progressCard.innerHTML = `
    <span>CAMPAIGN</span>
    <strong>${summary.completedCount}/${CAMPAIGN_LEVEL_COUNT}</strong>
    <em>${summary.totalStars}/${CAMPAIGN_STAR_MAX} ★</em>
  `;

  const play = forgedButton('PLAY', SHELL_ICONS.play, 'bf-shell-button--play', onPlay, {
    'aria-label': `Play Bolt Forge. Highest unlocked level ${summary.unlockedLevel}`,
  });

  const nav = el('nav', 'bf-home-nav', { 'aria-label': 'Bolt Forge menu' });
  nav.append(
    forgedButton('LEVELS', SHELL_ICONS.map, 'bf-shell-button--blue', onMap),
    forgedButton('HOW TO PLAY', SHELL_ICONS.help, 'bf-shell-button--green', onHelp),
    forgedButton('SETTINGS', SHELL_ICONS.settings, 'bf-shell-button--steel', onSettings),
  );

  const season = el('div', 'bf-home-season');
  season.innerHTML = `<span>♛</span><strong>SEASON 1</strong><small>Forge Legends</small>`;

  stage.append(brand, progressCard, buildHomeSculpture(), play, season, nav);
  return stage;
}

const MAP_POINTS = Object.freeze({
  1: [20, 79],
  2: [39, 70],
  3: [31, 60],
  4: [55, 53],
  5: [76, 59],
  6: [76, 44],
  7: [56, 37],
  8: [29, 31],
  9: [61, 25],
  10: [51, 14],
});

const MAP_ZONES = Object.freeze([
  Object.freeze({ id: 'apprentice', label: 'APPRENTICE FORGE', start: 1, end: 10 }),
  Object.freeze({ id: 'journeyman', label: 'JOURNEYMAN FORGE', start: 11, end: 20 }),
]);

function defaultMapZoneIndex(progress) {
  return Number(progress?.unlockedLevel || 1) > 10 ? 1 : 0;
}

function starRow(count) {
  const row = el('span', 'bf-map-node__stars', { 'aria-label': `${count} stars` });
  for (let i = 0; i < 3; i += 1) {
    row.append(el('span', i < count ? 'is-earned' : '', { text: '★' }));
  }
  return row;
}

function mapNode(number, slotNumber, progress, onPlayLevel) {
  const unlocked = number <= (Number(progress?.unlockedLevel) || 1);
  const record = progress?.completed?.[levelId(number)];
  const stars = Math.max(0, Math.min(3, Number(record?.stars) || 0));
  const [x, y] = MAP_POINTS[slotNumber];
  const node = el('button', `bf-map-node ${record?.completed ? 'is-complete' : ''} ${unlocked ? 'is-unlocked' : 'is-locked'} ${number === 20 ? 'is-forge-challenge' : ''}`.trim(), {
    type: 'button',
    'data-level-number': String(number),
    style: { '--map-x': `${x}%`, '--map-y': `${y}%` },
    disabled: !unlocked,
    'aria-label': unlocked ? `Play level ${number}, ${stars} stars` : `Level ${number} locked`,
  });

  if (!unlocked) {
    node.append(el('img', 'bf-map-node__lock', { src: SHELL_ICONS.lock, alt: '' }));
  } else {
    node.append(el('strong', 'bf-map-node__number', { text: String(number) }));
  }
  node.append(starRow(stars));
  if (unlocked) node.addEventListener('click', () => onPlayLevel(number));
  return node;
}

function mapPathSvg() {
  const svg = el('div', 'bf-map-path', { 'aria-hidden': 'true' });
  const route = 'M20 79 C27 78 33 74 39 70 S39 64 31 60 S42 56 55 53 S71 55 76 59 S80 51 76 44 S66 40 56 37 S36 35 29 31 S45 27 61 25 S61 18 51 14';
  svg.innerHTML = `
    <svg viewBox="0 0 100 100" preserveAspectRatio="none">
      <defs>
        <linearGradient id="bf-map-track-steel" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stop-color="#c7ced3"/>
          <stop offset="26%" stop-color="#7f898f"/>
          <stop offset="58%" stop-color="#4b555c"/>
          <stop offset="100%" stop-color="#252c31"/>
        </linearGradient>
      </defs>
      <path class="bf-map-path__shadow" d="${route}"/>
      <path class="bf-map-path__edge" d="${route}"/>
      <path class="bf-map-path__rail" d="${route}"/>
      <path class="bf-map-path__highlight" d="${route}"/>
      <path class="bf-map-path__joints" d="${route}"/>
      <path class="bf-map-path__rivets" d="${route}"/>
    </svg>`;
  return svg;
}

function mapBottomNav(onHome, onSettings) {
  const nav = el('nav', 'bf-map-bottom-nav', { 'aria-label': 'Map navigation' });
  nav.append(
    forgedButton('HOME', SHELL_ICONS.home, 'bf-shell-button--steel', onHome),
    forgedButton('MAP', SHELL_ICONS.map, 'bf-shell-button--gold is-active', () => {}, { 'aria-current': 'page' }),
    forgedButton('SETTINGS', SHELL_ICONS.settings, 'bf-shell-button--steel', onSettings),
  );
  return nav;
}

export function renderMap(root, {
  progress,
  onHome,
  onSettings,
  onPlayLevel,
  zoneIndex = null,
} = {}) {
  const summary = summarizeProgress(progress);
  const activeZoneIndex = zoneIndex == null
    ? defaultMapZoneIndex(progress)
    : Math.max(0, Math.min(MAP_ZONES.length - 1, Number(zoneIndex) || 0));
  const zone = MAP_ZONES[activeZoneIndex];

  const stage = mountShell(root, { screen: 'map', backdrop: MAP_BACKDROP });
  stage.dataset.mapZone = zone.id;
  stage.append(profileHud(summary));

  const title = el('header', 'bf-map-title');
  title.innerHTML = `
    <strong>BOLT <span>FORGE</span></strong>
    <em>CAMPAIGN MAP</em>
    <small>${zone.label} • LEVELS ${zone.start}–${zone.end}</small>
  `;

  const season = el('section', 'bf-map-season');
  season.innerHTML = `<span>${zone.id === 'journeyman' ? '⚒' : '♛'}</span><strong>${zone.id === 'journeyman' ? 'JOURNEYMAN' : 'SEASON 1'}</strong><small>${zone.id === 'journeyman' ? 'Think 2–3 moves ahead' : 'Forge Legends'}</small>`;

  const map = el('section', 'bf-map-board', { 'aria-label': `${zone.label} level map` });
  map.append(mapPathSvg());

  for (let number = zone.start; number <= zone.end; number += 1) {
    const slotNumber = number - zone.start + 1;
    map.append(mapNode(number, slotNumber, progress, onPlayLevel));
  }

  const currentNumber = summary.unlockedLevel;
  const current = map.querySelector(`[data-level-number="${currentNumber}"]`);
  if (current && !progress?.completed?.[levelId(currentNumber)]) {
    const marker = el('span', 'bf-map-current', { text: 'YOU ARE HERE' });
    current.append(marker);
  }

  if (zone.start === 1) {
    map.append(
      el('div', 'bf-map-reward bf-map-reward--5', { html: '<span>★</span><strong>LEVEL 5</strong><small>REWARD</small>' }),
      el('div', 'bf-map-reward bf-map-reward--10', { html: '<span>⚙</span><strong>LEVEL 10</strong><small>CHECKPOINT</small>' }),
    );
  } else {
    map.append(
      el('div', 'bf-map-reward bf-map-reward--15', { html: '<span>⚒</span><strong>LEVEL 15</strong><small>TRICK BOARD</small>' }),
      el('div', 'bf-map-reward bf-map-reward--20', { html: '<span>🔥</span><strong>LEVEL 20</strong><small>FORGE CHALLENGE</small>' }),
    );
  }

  const zoneNav = el('nav', 'bf-map-zone-nav', { 'aria-label': 'Campaign map zones' });
  const previous = el('button', 'bf-map-zone-button', {
    type: 'button',
    text: '◀ LEVELS 1–10',
    disabled: activeZoneIndex === 0,
  });
  const next = el('button', 'bf-map-zone-button bf-map-zone-button--next', {
    type: 'button',
    text: 'LEVELS 11–20 ▶',
    disabled: activeZoneIndex === MAP_ZONES.length - 1,
  });
  previous.addEventListener('click', () => renderMap(root, { progress, onHome, onSettings, onPlayLevel, zoneIndex: activeZoneIndex - 1 }));
  next.addEventListener('click', () => renderMap(root, { progress, onHome, onSettings, onPlayLevel, zoneIndex: activeZoneIndex + 1 }));
  zoneNav.append(previous, next);

  const stats = el('aside', 'bf-map-stats');
  stats.innerHTML = `
    <span>FORGE PROGRESS</span>
    <strong>${summary.completedCount}/${CAMPAIGN_LEVEL_COUNT} LEVELS</strong>
    <em>${summary.totalStars}/${CAMPAIGN_STAR_MAX} STARS</em>
  `;

  stage.append(title, season, map, zoneNav, stats, mapBottomNav(onHome, onSettings));

  if (new URLSearchParams(window.location.search).get('debug') === 'map-reference') {
    const reference = el('img', 'bf-map-reference-overlay', {
      src: MAP_BACKDROP,
      alt: '',
      'aria-hidden': 'true',
    });
    stage.append(reference);
  }

  return stage;
}

function overlayScreen(root, screen, titleText, backdrop = HOME_BACKDROP) {
  const stage = mountShell(root, { screen, backdrop });
  const panel = el('section', 'bf-shell-panel');
  panel.append(el('h1', '', { text: titleText }));
  stage.append(panel);
  return { stage, panel };
}

export function renderHelp(root, { onHome, onMap } = {}) {
  const { stage, panel } = overlayScreen(root, 'help', 'HOW TO PLAY');
  panel.append(
    el('p', 'bf-shell-panel__lead', { text: 'Clear every colored bolt before the move counter reaches zero.' }),
  );

  const cards = el('div', 'bf-help-grid');
  [
    ['1', 'UNSCREW', 'Select a reachable colored bolt. It spins free and drops into its matching color bin.'],
    ['2', 'REVEAL', 'Some bolts are hidden behind plates. Remove the right fasteners and the plate releases.'],
    ['3', 'PLAN', 'Each action costs a move. Watch the bin targets and use the cleanest removal order.'],
    ['4', 'BOOST', 'Magnet pulls two reachable bolts, Hammer removes a stubborn bolt, and Shuffle changes remaining colors.'],
  ].forEach(([number, title, copy]) => {
    const card = el('article', 'bf-help-card');
    card.innerHTML = `<span>${number}</span><strong>${title}</strong><p>${copy}</p>`;
    cards.append(card);
  });
  panel.append(cards);

  const actions = el('div', 'bf-shell-panel__actions');
  actions.append(
    forgedButton('HOME', SHELL_ICONS.home, 'bf-shell-button--steel', onHome),
    forgedButton('LEVEL MAP', SHELL_ICONS.map, 'bf-shell-button--gold', onMap),
  );
  panel.append(actions);
  return stage;
}

export function createShellSettingsStore(storage = globalThis?.localStorage) {
  const key = 'bolt-forge.shell-settings.v1';
  const defaults = { reducedMotion: false, highContrast: false };

  function read() {
    try {
      const raw = storage?.getItem?.(key);
      const parsed = raw ? JSON.parse(raw) : {};
      return { ...defaults, ...(parsed || {}) };
    } catch {
      return { ...defaults };
    }
  }

  function write(next) {
    const value = { ...defaults, ...read(), ...next };
    try { storage?.setItem?.(key, JSON.stringify(value)); } catch { /* no-op */ }
    return value;
  }

  return Object.freeze({ key, read, write });
}

export function applyShellSettings(settings) {
  const root = document.documentElement;
  root.dataset.bfReducedMotion = settings?.reducedMotion ? 'true' : 'false';
  root.dataset.bfHighContrast = settings?.highContrast ? 'true' : 'false';
}

export function renderSettings(root, {
  progress,
  settingsStore,
  onHome,
  onMap,
  onResetProgress,
} = {}) {
  const { stage, panel } = overlayScreen(root, 'settings', 'SETTINGS');
  const summary = summarizeProgress(progress);
  let settings = settingsStore.read();
  let resetArmed = false;

  const progressLine = el('div', 'bf-settings-progress', {
    html: `<span>Campaign</span><strong>${summary.completedCount}/${CAMPAIGN_LEVEL_COUNT} levels • ${summary.totalStars}/30 stars</strong>`,
  });
  panel.append(progressLine);

  function settingToggle(label, description, key) {
    const row = el('div', 'bf-setting-row');
    const copy = el('div', 'bf-setting-row__copy', { html: `<strong>${label}</strong><span>${description}</span>` });
    const button = el('button', `bf-toggle ${settings[key] ? 'is-on' : ''}`, {
      type: 'button',
      'aria-pressed': settings[key] ? 'true' : 'false',
      'aria-label': label,
      text: settings[key] ? 'ON' : 'OFF',
    });
    button.addEventListener('click', () => {
      settings = settingsStore.write({ [key]: !settings[key] });
      applyShellSettings(settings);
      button.classList.toggle('is-on', settings[key]);
      button.setAttribute('aria-pressed', settings[key] ? 'true' : 'false');
      button.textContent = settings[key] ? 'ON' : 'OFF';
    });
    row.append(copy, button);
    return row;
  }

  panel.append(
    settingToggle('Reduced Motion', 'Shortens non-essential UI and puzzle motion.', 'reducedMotion'),
    settingToggle('High Contrast', 'Strengthens outlines and panel separation.', 'highContrast'),
  );

  const reset = forgedButton('RESET CAMPAIGN', SHELL_ICONS.settings, 'bf-shell-button--red', () => {
    if (!resetArmed) {
      resetArmed = true;
      reset.querySelector('.bf-shell-button__label').textContent = 'TAP AGAIN TO RESET';
      window.setTimeout(() => {
        resetArmed = false;
        reset.querySelector('.bf-shell-button__label').textContent = 'RESET CAMPAIGN';
      }, 3500);
      return;
    }
    onResetProgress?.();
  });
  panel.append(reset);

  const actions = el('div', 'bf-shell-panel__actions');
  actions.append(
    forgedButton('HOME', SHELL_ICONS.home, 'bf-shell-button--steel', onHome),
    forgedButton('LEVEL MAP', SHELL_ICONS.map, 'bf-shell-button--gold', onMap),
  );
  panel.append(actions);
  return stage;
}
